import React, { useState, useCallback } from 'react';
import {
  View,
  Text,
  TextInput,
  StyleSheet,
  TextInputProps,
  StyleProp,
  ViewStyle,
  TextStyle,
  NativeSyntheticEvent,
  TextInputFocusEventData,
} from 'react-native';
import { useThemedStyles } from '@/hooks/shared/useThemeColors';

export interface GlassFormInputProps extends Omit<TextInputProps, 'style'> {
  label?: string;
  error?: string;
  required?: boolean;
  containerStyle?: StyleProp<ViewStyle>;
  inputStyle?: StyleProp<TextStyle>;
  labelStyle?: StyleProp<TextStyle>;
}

export const GlassFormInput: React.FC<GlassFormInputProps> = ({
  label,
  error,
  required = false,
  containerStyle,
  inputStyle,
  labelStyle,
  multiline,
  onFocus,
  onBlur,
  ...rest
}) => {
  const styles = useStyles();
  const [isFocused, setIsFocused] = useState(false);

  const handleFocus = useCallback(
    (e: NativeSyntheticEvent<TextInputFocusEventData>) => {
      setIsFocused(true);
      onFocus?.(e);
    },
    [onFocus],
  );

  const handleBlur = useCallback(
    (e: NativeSyntheticEvent<TextInputFocusEventData>) => {
      setIsFocused(false);
      onBlur?.(e);
    },
    [onBlur],
  );

  return (
    <View style={[styles.container, containerStyle]}>
      {label ? (
        <Text style={[styles.label, labelStyle]}>
          {label}
          {required ? <Text style={styles.required}> *</Text> : null}
        </Text>
      ) : null}
      <TextInput
        style={[
          styles.input,
          isFocused && !error && styles.inputFocused,
          error && styles.inputError,
          multiline && styles.textArea,
          inputStyle,
        ]}
        placeholderTextColor={styles.placeholder.color as string}
        multiline={multiline}
        onFocus={handleFocus}
        onBlur={handleBlur}
        {...rest}
      />
      {error ? <Text style={styles.errorText}>{error}</Text> : null}
    </View>
  );
};

GlassFormInput.displayName = 'GlassFormInput';

const useStyles = () =>
  useThemedStyles(({ components, semantic }) =>
    StyleSheet.create({
      container: {
        marginBottom: 16,
      },
      label: {
        fontSize: 14,
        fontWeight: '500',
        color: components.admin.formInput.label,
        marginBottom: 8,
      },
      required: {
        color: semantic.status.error,
      },
      input: {
        backgroundColor: components.admin.formInput.bg,
        borderRadius: 8,
        paddingHorizontal: 16,
        paddingVertical: 12,
        fontSize: 16,
        color: components.admin.formInput.text,
        borderWidth: 1,
        borderColor: components.admin.formInput.border,
        minHeight: 48,
      },
      inputFocused: {
        borderColor: components.admin.formInput.borderFocused,
      },
      inputError: {
        borderColor: semantic.status.error,
      },
      textArea: {
        minHeight: 120,
        textAlignVertical: 'top',
        paddingTop: 12,
      },
      placeholder: {
        color: components.admin.formInput.placeholder,
      },
      errorText: {
        fontSize: 12,
        color: semantic.status.error,
        marginTop: 4,
      },
    }),
  );
